import { useEffect, useState } from "react"
import { Box, Typography, Button, Card, CardContent, CircularProgress, Fade, Zoom, Chip } from "@mui/material"
import { styled, keyframes } from "@mui/system"
import { useParams, useNavigate } from "react-router-dom"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import PhoneIcon from "@mui/icons-material/Phone"
import PeopleIcon from "@mui/icons-material/People"
import StarIcon from "@mui/icons-material/Star"
import FlashOnIcon from "@mui/icons-material/FlashOn"
import { API_URL } from './config';

// אנימציות
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
  }
`

const gentleGlowAnimation = keyframes`
  0%, 100% {
    text-shadow: 0 0 5px rgba(255, 255, 255, 0.3), 0 0 10px rgba(33, 150, 243, 0.2);
  }
  50% {
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.5), 0 0 15px rgba(33, 150, 243, 0.3);
  }
`

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`

const sparkleAnimation = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`

const rotateAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`

const waitingDotsAnimation = keyframes`
  0%, 20% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`

// קונטיינר ראשי עם רקע גרדיאנט דרמטי
const GameContainer = styled(Box)({
  minHeight: "100vh",
  background: "linear-gradient(135deg, #0c0c0c 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #533483 100%)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "radial-gradient(circle at 50% 50%, rgba(33, 150, 243, 0.1) 0%, transparent 50%)",
    pointerEvents: "none",
  },
})

// כותרת ראשית בתוך כרטיס מעוצב
const TitleCard = styled(Card)({
  background: "rgba(255, 255, 255, 0.05)",
  backdropFilter: "blur(15px)",
  border: "2px solid rgba(33, 150, 243, 0.3)",
  borderRadius: "25px",
  padding: "1rem 2rem",
  marginBottom: "3rem",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
})

const MainTitle = styled(Typography)({
  fontSize: "3.5rem",
  fontWeight: "bold",
  color: "#ffffff",
  textAlign: "center",
  animation: `${gentleGlowAnimation} 3s ease-in-out infinite alternate`,
  background: "linear-gradient(45deg, #2196f3, #21cbf3, #2196f3)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  "@media (max-width: 600px)": {
    fontSize: "2.5rem",
  },
})

// כרטיס מידע מעוצב
const InfoCard = styled(Card)({
  background: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: "20px",
  minWidth: "250px",
  minHeight: "200px",
  animation: `${floatAnimation} 3s ease-in-out infinite`,
  "&:hover": {
    transform: "scale(1.05)",
    transition: "transform 0.3s ease",
  },
})

// כפתור התחלה דרמטי
const StartButton = styled(Button)({
  background: "linear-gradient(45deg, #ff6b6b, #ee5a24, #ff6b6b)",
  border: "none",
  borderRadius: "50px",
  color: "white",
  fontSize: "1.5rem",
  fontWeight: "bold",
  padding: "15px 40px",
  margin: "2rem 0",
  minWidth: "250px",
  animation: `${pulseAnimation} 2s infinite`,
  "&:hover": {
    background: "linear-gradient(45deg, #ee5a24, #ff6b6b, #ee5a24)",
    transform: "scale(1.1)",
  },
  "&:disabled": {
    background: "rgba(255, 255, 255, 0.3)",
    animation: "none",
  },
})

// קומפוננטת כוכבים מנצנצים
const SparkleEffect = () => {
  return (
    <>
      {[...Array(15)].map((_, i) => (
        <StarIcon
          key={i}
          sx={{
            position: "absolute",
            color: "#ffd700",
            fontSize: Math.random() * 20 + 10,
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `${sparkleAnimation} ${2 + Math.random() * 3}s infinite`,
            animationDelay: `${Math.random() * 2}s`,
            zIndex: 1,
          }}
        />
      ))}
    </>
  )
}

// קומפוננטת ממתין לשחקנים מעוצבת
const WaitingComponent = () => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        marginTop: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <FlashOnIcon
          sx={{
            color: "#ffd700",
            fontSize: "2rem",
            animation: `${rotateAnimation} 2s linear infinite`,
          }}
        />
        <Typography
          variant="h5"
          sx={{
            color: "#fff",
            fontWeight: "bold",
            background: "linear-gradient(45deg, #ffd700, #ff6b6b, #2196f3)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          ממתין לשחקנים
        </Typography>
        <FlashOnIcon
          sx={{
            color: "#ffd700",
            fontSize: "2rem",
            animation: `${rotateAnimation} 2s linear infinite reverse`,
          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1 }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#2196f3",
              animation: `${waitingDotsAnimation} 1.5s infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </Box>
    </Box>
  )
}

const ToPlay = () => {
  const [connectedPlayers, setConnectedPlayers] = useState(0)
  const [gameName, setGameName] = useState("")
  const [number, setNumber] = useState("")
  const [isStarting, setIsStarting] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const navigate = useNavigate()
  const { gameId } = useParams()
  const [isFullscreen, setIsFullscreen] = useState(false)

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement
        .requestFullscreen()
        .then(() => setIsFullscreen(true))
        .catch((err) => console.error("Failed to enter fullscreen:", err))
    } else if (document.exitFullscreen) {
      document
        .exitFullscreen()
        .then(() => setIsFullscreen(false))
        .catch((err) => console.error("Failed to exit fullscreen:", err))
    }
  }
useEffect(() => {
  // עצירת אודיו קודם
  if (window.currentAudio) {
    try {
      window.currentAudio.pause();
      window.currentAudio.currentTime = 0;
    } catch (e) {}
  }

  // יצירת אודיו חדש
  const audio = new Audio("/music/waiting.mp3");
  audio.loop = true;
  audio.volume = 0.5;
  audio.play().catch(console.error);

  // שמירה גלובלית כדי שנוכל לעצור אותו מאוחר יותר
  window.currentAudio = audio;

  // ניקוי בעת עזיבת הדף
  return () => {
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch (e) {}
  };
}, []);
useEffect(() => {
  if (!document.fullscreenElement) {
    toggleFullscreen();
  }
    if (gameId) {
      // הכנת המשחק — איפוס סטטוס + מחוברים כדי שמובייל יראה "ממתין"
      fetch(`${API_URL}/prepare_game/${gameId}`, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      }).catch((err) => console.error("שגיאה בהכנת המשחק:", err));

      fetch(`${API_URL}/get_info/${gameId}`)
        .then((response) => response.json())
        .then((data) => {
          setGameName(data.name)
          setNumber(data.number)
          setConnectedPlayers(data.connectedPlayers)
        })
        .catch((error) => {
          console.error("שגיאה בקבלת נתונים:", error)
        })

      const interval = setInterval(() => {
        fetch(`${API_URL}/get_info/${gameId}`)
          .then((response) => response.json())
          .then((data) => {
            setConnectedPlayers(data.connectedPlayers)
          })
          .catch((error) => {
            console.error("שגיאה בקבלת נתונים:", error)
          })
      }, 1000)

      return () => {
        clearInterval(interval)
      }
    } else {
      console.error("gameId לא נמצא!")
    }
  }, [gameId])

  const startGame = () => {
// שלב 1: עצור כל אודיו קודם
if (window.currentAudio) {
  try {
    window.currentAudio.pause();
    window.currentAudio.currentTime = 0;
  } catch (e) {}
}

// שלב 2: הפעל את /music/4321.wav
const audio = new Audio("/music/4321.wav");
audio.loop = true;
audio.volume = 0.5;
audio.play().catch(console.error);
window.currentAudio = audio;

// שלב 3: הפעל את האנימציה/הספירה
setIsStarting(true);
setCountdown(4);

// שלב 4: עצור את האודיו אחרי זמן מסוים (נניח 4 שניות)
setTimeout(() => {
  try {
    audio.pause();
    audio.currentTime = 0;
  } catch (e) {}
}, 4000);

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval)
          fetch(`${API_URL}/start_game/${gameId}`, {
            method: "POST",
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
          })
            .then((response) => response.json())
            .then((data) => {
              if (data.error === 'no_questions') {
                alert("לא ניתן להפעיל משחק ללא שאלות. הוסף שאלות תחילה בפאנל הניהול.")
                setIsStarting(false)
              } else if (data.error === 'no_package') {
                alert("אין לך הפעלות זמינות. יש לרכוש חבילה כדי להפעיל משחק.")
                setIsStarting(false)
              } else if (data.error === 'too_many_players') {
                alert(`💡 החבילה שלך תומכת ב-${data.maxPlayers} שחקנים, אבל יש לך ${data.totalPlayers} שחקנים שמורים.\n\nשדרג לחבילה גדולה יותר או הסר שחקנים מהרשימה בפאנל הניהול.`)
                setIsStarting(false)
              } else if (data.re) {
                localStorage.setItem('connectedPlayers', connectedPlayers);
                navigate(`/play/${gameId}`)
              } else {
                alert("לא ניתן להפעיל את המשחק, נסה שנית")
                setIsStarting(false)
              }
            })
            .catch((error) => {
              localStorage.setItem('connectedPlayers', connectedPlayers);
              navigate(`/play/${gameId}`)
              console.error("שגיאה בהפעלת המשחק:", error)
            })
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  return (
    <GameContainer>
      {/* אפקטי כוכבים */}
      <SparkleEffect />

      {/* כפתור מסך מלא */}
      <Button
        onClick={toggleFullscreen}
        sx={{
          position: "absolute",
          top: "20px",
          right: "20px",
          zIndex: 1000,
          background: "rgba(255, 255, 255, 0.2)",
          backdropFilter: "blur(10px)",
          color: "#fff",
          borderRadius: "50%",
          minWidth: "50px",
          height: "50px",
          "&:hover": {
            background: "rgba(255, 255, 255, 0.3)",
            transform: "scale(1.1)",
          },
        }}
      >
        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </Button>

<Box
  sx={{
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh", // הגובה המלא של המסך
    width: "100%",
    boxSizing: "border-box",
    px: 2,
  }}
>
  {/* כותרת – תמיד תופסת את המקום העליון */}
  <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <Fade in timeout={1000}>
      <TitleCard>
        <MainTitle variant="h1">ברוכים הבאים למשחק</MainTitle>
      </TitleCard>
    </Fade>
  </Box>

  {/* ריבועים – תמיד במרכז הגובה */}
{!isStarting && (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      alignItems: "center",
      justifyContent: "center",
      gap: 3,
    }}
  >
        {/* מספר החיוג */}
        <Zoom in timeout={1200}>
          <InfoCard sx={{ minWidth: "200px", minHeight: "140px" }}>
            <CardContent sx={{ textAlign: "center", padding: "1.5rem" }}>
              <PhoneIcon
                sx={{
                  fontSize: "2.5rem",
                  color: "#2196f3",
                  marginBottom: 1,
                  filter: "drop-shadow(0 0 10px rgba(33, 150, 243, 0.7))",
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  color: "#fff",
                  fontWeight: "bold",
                  textShadow: "0 0 15px rgba(255, 255, 255, 0.8)",
                  marginBottom: 2,
                }}
              >
                {number}
              </Typography>
              <Chip
                label="מספר לחיוג"
                sx={{
                  background: "linear-gradient(45deg, #2196f3, #21cbf3)",
                  color: "white",
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                }}
              />
            </CardContent>
          </InfoCard>
        </Zoom>

        {/* שחקנים מחוברים */}
        <Zoom in timeout={1400}>
          <InfoCard sx={{ minWidth: "200px", minHeight: "140px" }}>
            <CardContent sx={{ textAlign: "center", padding: "1.5rem" }}>
              <PeopleIcon
                sx={{
                  fontSize: "2.5rem",
                  color: "#4caf50",
                  marginBottom: 1,
                  filter: "drop-shadow(0 0 10px rgba(76, 175, 80, 0.7))",
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  color: "#fff",
                  fontWeight: "bold",
                  textShadow: "0 0 15px rgba(255, 255, 255, 0.8)",
                  marginBottom: 2,
                }}
              >
                {connectedPlayers}
              </Typography>
              <Chip
                label="שחקנים מחוברים"
                sx={{
                  background: "linear-gradient(45deg, #4caf50, #66bb6a)",
                  color: "white",
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                }}
              />
            </CardContent>
          </InfoCard>
        </Zoom>
      </Box>

)}
  {/* כפתור או טיימר – בתחתית של הגובה */}
  <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start" }}>
    <Zoom in timeout={1600}>
      <Box sx={{ textAlign: "center" }}>
        {isStarting ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress
              size={100}
              sx={{
                color: "#ff6b6b",
                filter: "drop-shadow(0 0 20px rgba(255, 107, 107, 0.8))",
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: "#fff",
                fontWeight: "bold",
                textShadow: "0 0 30px rgba(255, 255, 255, 1)",
                fontSize: "4rem",
              }}
            >
              {countdown}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: "#ffd700",
                fontWeight: "bold",
                textShadow: "0 0 10px rgba(255, 215, 0, 0.8)",
              }}
            >
              המשחק מתחיל
            </Typography>
          </Box>
        ) : (
          <StartButton
            onClick={startGame}
            startIcon={<PlayArrowIcon sx={{ fontSize: "2rem" }} />}
            disabled={connectedPlayers === 0}
          >
            התחל משחק
          </StartButton>
        )}

        {/* מחכה לשחקנים */}
        <Box
          sx={{
            mt: 2,
            opacity: connectedPlayers === 0 && !isStarting ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
            pointerEvents: "none",
          }}
        >
          <WaitingComponent />
        </Box>
      </Box>
    </Zoom>
  </Box>
</Box>

      {/* אפקטים דקורטיביים משופרים */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          left: "10%",
          width: "150px",
          height: "150px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(33, 150, 243, 0.3) 0%, transparent 70%)",
          animation: `${floatAnimation} 4s ease-in-out infinite`,
          zIndex: -1,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          bottom: "15%",
          right: "15%",
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255, 107, 107, 0.2) 0%, transparent 70%)",
          animation: `${floatAnimation} 5s ease-in-out infinite reverse`,
          zIndex: -1,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "5%",
          width: "100px",
          height: "100px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255, 215, 0, 0.2) 0%, transparent 70%)",
          animation: `${floatAnimation} 6s ease-in-out infinite`,
          zIndex: -1,
        }}
      />
    </GameContainer>
  )
}

export default ToPlay
